import React, { Component } from 'react';
import { bool, func, object, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

// Import configs and util modules
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { useHistory, useLocation } from 'react-router-dom';
import { propTypes } from '../../util/types';
import { GUIDE_LISTING_TYPE, REVIEW_LISTING_TYPE, NEWS_LISTING_TYPE, ARTICLE_LISTING_TYPES } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import {
    isMainSearchTypeKeywords,
    isOriginInUse,
} from '../../util/search';
import { createResourceLocatorString } from '../../util/routes';
import {
    createSearchResultSchema,
    pickListingFieldFilters,
    searchParamsPicker,

} from '../SearchPage/SearchPage.shared';
// Import shared components
import { H1, H3, Page, PaginationLinks } from '../../components';
import FeaturedArticleCard from './FeaturedArticleCard/FeaturedArticleCard';
import ArticleCard from './ArticleCard/ArticleCard';
import GuideCarousel from './GuideCarousel/GuideCarousel';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
// Import SearchForm component
import SearchForm from '../NotFoundPage/SearchForm/SearchForm';

// Import modules from this directory
import { loadData } from './ArticlesPage.duck';
import css from './ArticlesPage.module.css';

export class ArticlesPageComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            componentMounted: false,
        };
    }

    componentDidMount() {
        this.setState({ componentMounted: true });
    }

    render() {
        const {
            intl,
            articles,
            scrollingDisabled,
            routeConfiguration,
            pagination,
            config,
            searchInProgress,
            location,
            searchParams = {},
            params: currentPathParams = {},
        } = this.props;

        const { listingType: listingTypePathParam } = currentPathParams;

        const { listingFields } = config?.listing || {};
        const { defaultFilters: defaultFiltersRaw, sortConfig, mainSearch } = config?.search || {};

        const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
        const defaultFiltersConfig = listingTypePathParam
            ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
            : defaultFiltersRaw;

        const categoryConfiguration = config.categoryConfiguration;
        const listingCategories = categoryConfiguration.categories;
        const listingFieldsConfig = pickListingFieldFilters({
            listingFields,
            locationSearch: location.search,
            categoryConfiguration,
            activeListingTypes,
            currentPathParams,
        });
        const filterConfigs = {
            listingFieldsConfig,
            defaultFiltersConfig,
            listingCategories,
            activeListingTypes,
            currentPathParams,
            mainSearch,
        };

        // Page transition might initially use values from previous search
        // urlQueryParams doesn't contain page specific url params
        // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
        const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
            location.search,
            searchParams,
            filterConfigs,
            sortConfig,
            mainSearch,
            isOriginInUse(config)
        );
        const validQueryParams = urlQueryParams;

        const hasPaginationInfo = !!pagination && pagination.totalItems != null;

        const totalItems =
            searchParamsAreInSync && hasPaginationInfo
                ? pagination.totalItems
                : pagination?.paginationUnsupported
                    ? listings.length
                    : 0;

        const listingsAreLoaded =
            !searchInProgress &&
            searchParamsAreInSync &&
            !!(hasPaginationInfo || pagination?.paginationUnsupported);
        // Parse page heading to be included in the title
        const pageHeading = searchInProgress
            ? intl.formatMessage({ id: 'ArticlePanelHeader.loadingResults' })
            : intl.formatMessage({ id: 'ArticlePanelHeader.foundResults' }, { count: totalItems });

        const { title, description, schema } = createSearchResultSchema(
            articles,
            searchParamsInURL || {},
            intl,
            routeConfiguration,
            config,
            pageHeading
        );

        const reviews = articles.filter(article =>
            article?.attributes?.publicData?.listingType === REVIEW_LISTING_TYPE
        );
        const guides = articles.filter(article =>
            article?.attributes?.publicData?.listingType === GUIDE_LISTING_TYPE
        );
        const newsArticles = articles.filter(article =>
            article?.attributes?.publicData?.listingType === NEWS_LISTING_TYPE
        );
        // Separate featured article (most recent) from others
        const [featuredReview, ...otherReviews] = reviews || [];

        const topbarClasses = this.state.isMobileModalOpen
            ? classNames(css.topbarBehindModal, css.topbar)
            : css.topbar;

        const handleSearchSubmit = values => {
            const { history } = this.props;
            const { keywords, location } = values;
            const { search, selectedPlace } = location || {};
            const { origin, bounds } = selectedPlace || {};
            // Create base search params with article listing types filter
            const baseSearchParams = {
                pub_listingType: ARTICLE_LISTING_TYPES.join(','),
            };
            // Add keywords or location based on search type
            const searchParams = keywords
                ? { ...baseSearchParams, keywords }
                : { ...baseSearchParams, address: search, origin, bounds };
            // Navigate to SearchPage with the search parameters
            history.push(
                createResourceLocatorString('ArticlesPage', routeConfiguration, {}, searchParams)
            );
        };
        return (
            <Page
                scrollingDisabled={scrollingDisabled}
                description={description}
                title={title}
                schema={schema}
            >
                <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
                <div className={css.pageOverlay} />
                <div className={css.layoutWrapperContainer}>
                    <div className={css.layoutWrapperMain}>
                        <div className={css.titleBarWrapper}>
                            <H1 as="h1" className={css.pageTitle}>
                                {intl.formatMessage({ id: 'ArticlesPage.heading' })}
                            </H1>
                            <div className={css.searchFormWrapper}>
                                <SearchForm
                                    className={css.searchForm}
                                    isKeywordSearch={isMainSearchTypeKeywords(config)}
                                    placeholderMessageId="ArticlesPage.SearchForm.placeholder"
                                    onSubmit={handleSearchSubmit}
                                />
                            </div>
                        </div>
                        <div className={css.contentWrapperMain}>
                            <div className={css.content}>
                                {articles && articles.length > 0 && (
                                    <div className={css.articlesLayout}>
                                        <div className={css.leftColumn}>
                                            {/* Featured Article */}
                                            {featuredReview && (
                                                <div className={css.featuredArticle}>
                                                    <FeaturedArticleCard listing={featuredReview} intl={intl} config={config} />
                                                </div>

                                            )}
                                            {/* Featured News Carousel  */}
                                            {newsArticles && newsArticles.length > 0 && (
                                                <div className={css.latestNewsSection}>
                                                    <H3 as="h3" className={css.latestNewsTitle}>
                                                        {intl.formatMessage({ id: 'ArticlesPage.featuredNews' })}
                                                    </H3>
                                                    {/* We repurpose the Guide Carousel for news articles */}
                                                    <GuideCarousel guides={newsArticles} intl={intl} config={config} itemsToShow={2}/>
                                                </div>
                                            )}
                                            {/* Featured Guides Carousel */}
                                            {guides && guides.length > 0 && (
                                                <div className={css.featuredGuidesSection}>
                                                    <H3 as="h3" className={css.featuredGuidesTitle}>
                                                        {intl.formatMessage({ id: 'ArticlesPage.featuredGuides' })}
                                                    </H3>
                                                    <GuideCarousel guides={guides} intl={intl} config={config} itemsToShow={1} />
                                                </div>
                                            )}
                                        </div>
                                        {/* Other Articles */}
                                        {otherReviews && otherReviews.length > 0 && (
                                            <div className={css.articlesList}>
                                                {otherReviews.map(article => (
                                                    <ArticleCard
                                                        key={article.id.uuid}
                                                        listing={article}
                                                        intl={intl}
                                                        config={config}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {newsArticles?.length > 0 && (
                                            <div className={css.mobileNewsList}>
                                                {/* <H3 as="h3" className={css.latestNewsTitle}>
                                                {intl.formatMessage({ id: 'ArticlesPage.featuredNews' })}
                                                </H3> */}
                                                {newsArticles.map(article => (
                                                    <ArticleCard
                                                        key={article.id.uuid}
                                                        listing={article}
                                                        intl={intl}
                                                        config={config}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(!articles || articles.length === 0) && (
                                    <div className={css.noResults}>
                                        <p>{intl.formatMessage({ id: 'ArticlesPage.noArticles' })}</p>
                                    </div>
                                )}
                                {listingsAreLoaded && pagination && pagination.totalPages > 1 && (
                                    <PaginationLinks
                                        className={css.pagination}
                                        pageName="ArticlesPage"
                                        pagePathParams={{}}
                                        pageSearchParams={parse(location.search)}
                                        pagination={pagination}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <FooterContainer />
            </Page>
        );
    }
}

const EnhancedArticlesPage = props => {
    const config = useConfiguration();
    const routeConfiguration = useRouteConfiguration();
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();

    return (
        <ArticlesPageComponent
            config={config}
            routeConfiguration={routeConfiguration}
            intl={intl}
            history={history}
            location={location}
            {...props}
        />
    );
};

const mapStateToProps = state => {
    const { currentPageResultIds, pagination, searchInProgress } = state.ArticlesPage;
    const articles = getListingsById(state, currentPageResultIds);

    return {
        articles,
        pagination,
        searchInProgress,
        scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    onManageDisableScrolling: (componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ArticlesPage = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(EnhancedArticlesPage);

ArticlesPage.loadData = loadData;

export default ArticlesPage;
